/**
 * The effects half of stage-following: re-read the case tree when a stage
 * finishes, then point the viewport at what it produced. The rules, and the
 * reasoning behind them, are in followRules.ts.
 */

import { refreshCaseNodes } from "@/ipc/api";
import type { Modality, StageId } from "@/ipc/contract";
import { appActions, useAppStore } from "@/store/appStore";
import {
  followJob,
  hasStageView,
  noteApplied,
  pickStageView,
  stillFollowing,
  type StageView,
} from "./followRules";
import { useViewerStore } from "./viewerStore";

export { resetFollowing, stillFollowing } from "./followRules";
export type { StageView } from "./followRules";

export function applyStageView(view: StageView): void {
  const viewer = useViewerStore.getState();
  viewer.setBaseNode(view.base.id);
  viewer.setOverlayNodes(view.overlay ? [view.overlay.id] : []);
  if (view.style) {
    useViewerStore.setState({
      overlayVisible: true,
      overlayColormap: view.style.colormap,
      overlayOpacity: view.style.opacity,
      // A binary mask has one non-zero value; 0.5 also keeps a probability map
      // at its 50% contour if one ever lands here.
      thresholdMin: 0.5,
      thresholdMax: 1,
    });
  }
  noteApplied(view.base.id);
}

/**
 * Called once per stage completion. Best-effort from end to end: a refresh that
 * fails must not turn a stage that really did finish into an error.
 */
export async function followCompletedStage(
  jobId: string,
  caseId: string,
  modality: Modality,
  stage: StageId,
): Promise<void> {
  followJob(jobId);
  if (!hasStageView(modality, stage)) return;
  if (!stillFollowing(useViewerStore.getState().baseNodeId)) return;

  const open = useAppStore.getState().currentCase;
  if (!open || open.caseId !== caseId) return;

  try {
    const updated = await refreshCaseNodes(caseId);
    // The reader may have closed or switched case while the stage finished.
    if (useAppStore.getState().currentCase?.caseId !== caseId) return;
    appActions.setCurrentCase(updated);
    if (!stillFollowing(useViewerStore.getState().baseNodeId)) return;
    const view = pickStageView(updated, modality, stage);
    if (view) applyStageView(view);
  } catch {
    // Leave the tree and the view as they were.
  }
}
